import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNotNil} from 'ramda-adjunct';

import {AuctionVehicleDetailResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {calculatePriceExclVat} from '../../../utils/calculatePriceExclVat';
import {isHiddenAuctionType} from '../../../utils/isHiddenAuctionType';
import {AuctionTypeBadge} from '../../AuctionTypeBadge/AuctionTypeBadge';
import {SealedAuctionPriceCard} from './SealedAuctionPriceCard';

interface AuctionCurrentBidProps extends TestIdProps {
  auction: AuctionVehicleDetailResponseBody;
}

export function AuctionCurrentBid(props: AuctionCurrentBidProps) {
  const formatCurrency = useFormatCurrency();
  const auction = props.auction;
  const auctionVehicle = auction.auctionVehicle;
  const isHiddenType = isHiddenAuctionType(auctionVehicle.auctionType);
  const isVatDeductible = !!auctionVehicle.vatReclaimable;
  const showConverted =
    auctionVehicle.currency !== auctionVehicle.originalCurrency &&
    isNotNil(auctionVehicle.originalPriceActual);
  const priceExclVat = isVatDeductible
    ? calculatePriceExclVat(auctionVehicle.originalPriceActual ?? null, auctionVehicle.vatRate)
    : null;
  let displayedPrice = EMPTY_PLACEHOLDER;
  if (isNotNil(auctionVehicle.priceActual)) {
    displayedPrice =
      formatCurrency(auctionVehicle.priceActual, auctionVehicle.currency ?? '') ??
      EMPTY_PLACEHOLDER;
  }
  if (isHiddenType) {
    return (
      <VStack spacing={3}>
        <AuctionTypeBadge
          auctionType={auctionVehicle.auctionType}
          showDescription
          data-testid={suffixTestId('auctionType', props)}
        />
        <SealedAuctionPriceCard
          auctionVehicle={auctionVehicle}
          data-testid={props['data-testid']}
        />
      </VStack>
    );
  }

  return (
    <Box backgroundColor="palettes.neutral.20.100" padding={4}>
      <VStack align="flex-end" spacing={1}>
        <AuctionTypeBadge
          auctionType={auctionVehicle.auctionType}
          showDescription
          data-testid={suffixTestId('auctionType', props)}
        />
        <Text size="xSmall" color="secondary" data-testid={suffixTestId('currentBidLabel', props)}>
          {i18n.t('entity.auction.labels.currentBid')}
        </Text>
        {/* A price never breaks at its thousands separator: each one stays on a single line and
            the converted price wraps under the current bid when the two no longer fit side by side. */}
        <HStack spacing={1} align="baseline" wrap justify="flex-end">
          <Heading
            size={2}
            alternative
            isSingleLine
            data-testid={suffixTestId('currentBidPrice', props)}
          >
            {displayedPrice}
          </Heading>
          <Show when={showConverted}>
            <Heading
              size={2}
              color="tertiary"
              alternative
              isSingleLine
              data-testid={suffixTestId('convertedPrice', props)}
            >
              {`/ ${formatCurrency(auctionVehicle.originalPriceActual ?? 0, auctionVehicle.originalCurrency ?? '') ?? EMPTY_PLACEHOLDER}`}
            </Heading>
          </Show>
        </HStack>
        <Show when={isVatDeductible && isNotNil(priceExclVat)}>
          <Text size="xSmall" color="secondary" data-testid={suffixTestId('priceExclVat', props)}>
            {formatCurrency(priceExclVat ?? 0, auctionVehicle.originalCurrency ?? '') ??
              EMPTY_PLACEHOLDER}{' '}
            {i18n.t('general.labels.withoutVat')}
          </Text>
        </Show>
      </VStack>
    </Box>
  );
}
